import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { fetchTargetingSummary, queryCustomers } from '../lib/api';

const fmt = n => n == null ? '·' : n.toLocaleString('en-IN');

const SEG_ORDER = ['Champions/VIP','Loyal regulars','New/in-window','New/past-window','Lapsed/cold','At-risk return-heavy'];
const SEG_META = {
  'Champions/VIP':        { badge: 'HANDLE',  cls: 'handle' },
  'Loyal regulars':       { badge: 'RETAIN',  cls: 'retain' },
  'New/in-window':        { badge: 'GROWTH',  cls: 'growth' },
  'New/past-window':      { badge: 'GROWTH',  cls: 'growth' },
  'Lapsed/cold':          { badge: 'WATCH',   cls: 'watch' },
  'At-risk return-heavy': { badge: 'HANDLE',  cls: 'handle' },
};

export default function TargetingTab({ filters, entityKey }) {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(true);
  const [reachInput, setReachInput] = useState('');
  const [reachDays, setReachDays] = useState(null);
  const [segment, setSegment] = useState(null);
  const [crossSellOnly, setCrossSellOnly] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      setBusy(true);
      const { data } = await fetchTargetingSummary({
        category: filters.category === 'ALL' ? null : filters.category,
        channel: filters.channel,
        region: filters.region,
        reachDays, crossSellOnly,
      });
      setSummary(data); setBusy(false);
    })();
  }, [filters.category, filters.channel, filters.region, reachDays, crossSellOnly]);

  const applyReach = () => {
    const v = parseInt(reachInput, 10);
    setReachDays(isNaN(v) || v <= 0 ? null : v);
  };
  const clearReach = () => { setReachInput(''); setReachDays(null); };

  const doExport = async () => {
    setExporting(true);
    // Pull ALL matching customers server-side, then export
    const { data, error } = await queryCustomers({
      category: filters.category === 'ALL' ? null : filters.category,
      channel: filters.channel === 'Omni' ? null : filters.channel,
      region: filters.region === 'ALL' ? null : filters.region,
      segment, reachDays, crossSellOnly,
      limit: null,
    });
    if (error) { alert('Export failed: ' + error.message); setExporting(false); return; }
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Customers');
    const parts = ['HUFT_customers', filters.category, filters.channel, segment || 'all', reachDays ? `reach${reachDays}d` : 'allreach'];
    XLSX.writeFile(wb, parts.filter(Boolean).join('_').replace(/[\s/]+/g,'_') + '.xlsx');
    setExporting(false);
  };

  if (busy || !summary) return <div className="loading">Loading targeting…</div>;

  const total = summary.total;
  return (
    <>
      <div className="explainer">
        <b>Customer-level export</b>. Click Export customers (Excel) to download every filtered customer with all attributes.
        Filter by brand, channel, segment, cross-sell, or reachability (below) to shape the export.
      </div>

      <div className="panel panel-lead">
        <div className="panel-head">
          <div>
            <div className="panel-title">Segments <span style={{color:'var(--orange)'}}>· click to drill in</span></div>
            <div className="panel-sub">
              <b>{fmt(total)}</b> customers in this selection · click a segment card to isolate
              {reachDays != null && <span style={{color:'var(--orange-dark)'}}> · reachability ≤ {reachDays} days</span>}
            </div>
          </div>
          <div className="panel-actions">
            <button className="icon-btn primary" onClick={doExport} disabled={exporting}>
              {exporting ? 'Exporting…' : '↓ Export customers (Excel)'}
            </button>
          </div>
        </div>

        <div style={{
          display:'flex', alignItems:'center', gap:14, marginBottom:14,
          padding:'10px 14px', background:'var(--cream-2)',
          border:'1px dashed var(--hairline-2)', borderRadius:6,
        }}>
          <span className="filter-label">Active in last</span>
          <input type="number" min="1" max="500" placeholder="e.g. 180" value={reachInput}
                 onChange={e => setReachInput(e.target.value)}
                 style={{ width:90, padding:'6px 10px', border:'1px solid var(--hairline-2)', borderRadius:5, fontSize:12.5 }} />
          <span className="filter-label" style={{textTransform:'none'}}>days</span>
          <button className="icon-btn" onClick={applyReach}>Apply</button>
          <button className="icon-btn" onClick={clearReach}>Clear</button>
          <span style={{ fontFamily:'var(--font-mono)', fontSize:11, color:'var(--muted)', marginLeft:8 }}>
            {reachDays == null
              ? 'Leave blank to include all customers. Restricts segments + Excel export to customers whose last purchase was within N days.'
              : `Active. Showing customers whose last purchase was within ${reachDays} days.`}
          </span>
        </div>

        <div className="seg-row">
          {SEG_ORDER.map(s => {
            const n = summary.segments?.[s] || 0;
            const meta = SEG_META[s];
            const active = segment === s;
            return (
              <button key={s} className={`seg-btn ${active ? 'active' : ''}`}
                      onClick={() => setSegment(active ? null : s)}>
                <span className={`badge ${meta.cls}`}>{meta.badge}</span>
                <div className="n">{fmt(n)}</div>
                <div className="lbl">{s}</div>
                <div className="pct">{total ? ((n/total*100).toFixed(0) + '%') : '-'} of selection</div>
              </button>
            );
          })}
        </div>

        <div className="overlay-row">
          <div className="overlay-card">
            <div>
              <div className="l">Cross-sell candidates</div>
              <div className="n">{fmt(summary.crossSellCount)}</div>
            </div>
            <div className="d">
              Customers with 2+ own-brand purchases whose most recent purchase was 3P. Layer own-brand messaging on top of the segment action.
              {' '}<a href="#" onClick={e => { e.preventDefault(); setCrossSellOnly(x => !x); }}
                style={{ color:'var(--violet)', fontWeight: 600 }}>
                {crossSellOnly ? 'Clear filter' : 'Filter →'}
              </a>
            </div>
          </div>
        </div>
      </div>

      {summary.actions && (
        <div className="panel">
          <div className="panel-head">
            <div>
              <div className="panel-title">Recommended actions</div>
              <div className="panel-sub">One action per segment. Ready to hand to CRM.</div>
            </div>
          </div>
          <table className="data-table">
            <thead><tr><th>Action</th><th className="num">Customers</th><th className="num">Share</th></tr></thead>
            <tbody>
              {Object.entries(summary.actions).sort((a,b) => b[1]-a[1]).map(([act, n]) => (
                <tr key={act}>
                  <td>{act}</td>
                  <td className="num strong">{fmt(n)}</td>
                  <td className="num" style={{color:'var(--muted)'}}>{total ? (n/total*100).toFixed(1) + '%' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
